/**
 * ds-primitives.tsx — 设计系统活文档的可复用文档原语
 *
 * 移植自原型 design/Novel-admin/src/pages/design-system.jsx 的 window.__ds
 * （DSHeader / DSBlock / DSDemo / DSToken / DSRule）+ patterns 文件里的 PrincipleCard。
 * 原型用 window.__ds 跨文件注册；这里改为具名 ESM export，由各 section 文件直接 import。
 * 视觉层（className / inline-style）与原型 1:1 保真。
 */

import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icons } from '~/lib/icons';

// ---------- DSHeader：章节大标题 ----------
interface DSHeaderProps {
  title: ReactNode;
  sub?: ReactNode;
  badge?: ReactNode;
  kicker?: ReactNode;
}
export function DSHeader({ title, sub, badge, kicker }: DSHeaderProps) {
  return (
    <div style={{ marginBottom: 20 }}>
      {kicker && (
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: 'var(--accent)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 6,
          }}
        >
          {kicker}
        </div>
      )}
      <div className="hstack" style={{ gap: 10, marginBottom: 6 }}>
        <h1
          style={{
            font: 'inherit',
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 600,
            color: 'var(--fg-strong)',
            letterSpacing: '-0.02em',
            margin: 0,
            lineHeight: 1.1,
          }}
        >
          {title}
        </h1>
        {badge}
      </div>
      {sub && (
        <div style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.55, maxWidth: 720 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ---------- DSBlock：带分割线标题的小节 ----------
interface DSBlockProps {
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}
export function DSBlock({ title, sub, children, action }: DSBlockProps) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div
        className="hstack"
        style={{ marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}
      >
        <h2 style={{ font: 'inherit', fontSize: 14, fontWeight: 600, margin: 0 }}>{title}</h2>
        {sub && <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>· {sub}</span>}
        <div style={{ flex: 1 }} />
        {action}
      </div>
      {children}
    </section>
  );
}

// ---------- DSDemo：Demo + 可折叠代码（仿 Ant Design 文档卡） ----------
interface DSDemoProps {
  title?: ReactNode;
  desc?: ReactNode;
  children: ReactNode;
  code?: string;
  defaultOpen?: boolean;
}
export function DSDemo({ title, desc, children, code, defaultOpen = false }: DSDemoProps) {
  const [show, setShow] = useState(defaultOpen);
  return (
    <div className="card" style={{ marginBottom: 12, overflow: 'hidden' }}>
      <div style={{ padding: '18px 18px 18px', background: 'var(--bg-elev)' }}>{children}</div>
      {(title || desc) && (
        <div
          style={{
            padding: '10px 18px',
            borderTop: '1px dashed var(--border)',
            background: 'var(--bg-subtle)',
          }}
        >
          {title && <div style={{ fontSize: 12, fontWeight: 500 }}>{title}</div>}
          {desc && (
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--fg-muted)',
                lineHeight: 1.55,
                marginTop: title ? 3 : 0,
              }}
            >
              {desc}
            </div>
          )}
        </div>
      )}
      {code && (
        <div>
          <button
            type="button"
            onClick={() => setShow(!show)}
            className="hstack"
            style={{
              width: '100%',
              gap: 6,
              padding: '8px 18px',
              border: 0,
              borderTop: '1px solid var(--border)',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 11,
              color: 'var(--fg-muted)',
              font: 'inherit',
              textAlign: 'left',
            }}
          >
            <Icons.IconChevRight
              size={11}
              style={{ transform: show ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}
            />
            {show ? '隐藏代码' : '查看代码'}
          </button>
          {show && (
            <pre
              style={{
                margin: 0,
                padding: '14px 18px',
                background: 'var(--bg-inset)',
                fontFamily: 'var(--font-mono)',
                fontSize: 11.5,
                lineHeight: 1.65,
                color: 'var(--fg)',
                overflow: 'auto',
                borderTop: '1px solid var(--border)',
              }}
            >
              <code>{code.trim()}</code>
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- DSToken：单行 token 展示（色板 / 间距 / 文字色等） ----------
interface DSTokenProps {
  label: ReactNode;
  value: ReactNode;
  swatch?: ReactNode;
  mono?: boolean;
}
export function DSToken({ label, value, swatch, mono = true }: DSTokenProps) {
  return (
    <div
      className="hstack"
      style={{ padding: '6px 10px', borderBottom: '1px solid var(--border)', gap: 12 }}
    >
      {swatch}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11.5, fontWeight: 500 }}>{label}</div>
      </div>
      <span className={mono ? 'mono' : ''} style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
        {value}
      </span>
    </div>
  );
}

// ---------- DSRule：DO / DON'T / NOTE 规则条 ----------
interface DSRuleProps {
  tone: 'do' | 'dont' | 'note';
  children: ReactNode;
}
export function DSRule({ tone, children }: DSRuleProps) {
  const toneColor: Record<DSRuleProps['tone'], string> = {
    do: 'pos',
    dont: 'neg',
    note: 'info',
  };
  const labels: Record<DSRuleProps['tone'], string> = {
    do: 'DO',
    dont: "DON'T",
    note: 'NOTE',
  };
  const c = toneColor[tone];
  return (
    <div
      style={{
        padding: '10px 14px',
        borderRadius: 6,
        background: `var(--${c}-soft)`,
        borderLeft: `3px solid var(--${c})`,
        fontSize: 12,
        color: 'var(--fg)',
        lineHeight: 1.55,
        marginBottom: 8,
      }}
    >
      <span
        style={{
          fontWeight: 600,
          color: `var(--${c})`,
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          letterSpacing: '0.06em',
          marginRight: 8,
        }}
      >
        {labels[tone]}
      </span>
      {children}
    </div>
  );
}

// ---------- PrincipleCard：设计原则卡（移植自 patterns 文件） ----------
interface PrincipleCardProps {
  name: ReactNode;
  // 原型部分原则卡只给 name + examples（无主描述），故 desc 可缺省
  desc?: ReactNode;
  examples?: ReactNode[];
}
export function PrincipleCard({ name, desc, examples }: PrincipleCardProps) {
  const ulStyle: CSSProperties = {
    fontSize: 11.5,
    color: 'var(--fg-muted)',
    lineHeight: 1.7,
    paddingLeft: 18,
    margin: '10px 0 0',
  };
  return (
    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
      <h3 style={{ font: 'inherit', fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>{name}</h3>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.65 }}>{desc}</div>
      {examples && (
        <ul style={ulStyle}>
          {examples.map((e, i) => (
            // 原则示例为静态有序文案，无稳定 id，按原型用 index 作 key
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
